/**
 * Shape recognition for "hold to snap" (spec §2, M3a design doc): RDP
 * polyline simplification plus line / rectangle / circle-ellipse fitting.
 * Framework-free; operates on packed [x, y, pressure, dt] Float32Array
 * buffers, matching stroke.ts / smoothing.ts / eraser.ts conventions.
 *
 * Priority when a stroke could plausibly match more than one family:
 * line, then rectangle, then circle/ellipse (first match wins).
 */

export interface Point2 { x: number; y: number }

const MIN_STROKE_LENGTH = 24; // px — shorter strokes never snap (spec §2)
const DEFAULT_EPSILON = 6; // px, RDP tolerance ("epsilon ~6px scaled" per spec)
const LINE_DEVIATION_FRAC = 0.08; // max perp deviation from chord, as a fraction of chord length
const CLOSED_GAP_FRAC = 0.15; // endpoint gap vs perimeter, for "closed-ish"
const RECT_ANGLE_TOLERANCE_DEG = 20; // corner interior angle must be 90 +/- this
const RECT_AXIS_TOLERANCE_DEG = 12; // dominant rotation within this of an axis -> snap to axis-aligned
const CIRCLE_MIN_CORNERS = 6; // simplified corner-count floor for the circle/ellipse family
const CIRCLE_RADIUS_VARIANCE_FRAC = 0.20; // max |r - mean| / mean across the raw stroke
const ELLIPSE_AXIS_DIFF_FRAC = 0.25; // x/y radius difference fraction that promotes circle -> ellipse
const CIRCLE_SEGMENTS = 48;

/**
 * Perpendicular distance from point p to the infinite line through a-b.
 * Falls back to point distance when a == b (degenerate segment).
 */
function perpDistance(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - ax, py - ay);
  return Math.abs(dy * px - dx * py + bx * ay - by * ax) / Math.sqrt(len2);
}

function rdpRange(pts: Point2[], start: number, end: number, epsilon: number, keep: Uint8Array): void {
  if (end <= start + 1) return;
  const a = pts[start]!, b = pts[end]!;
  let maxDist = -1, maxIdx = -1;
  for (let i = start + 1; i < end; i++) {
    const p = pts[i]!;
    const d = perpDistance(p.x, p.y, a.x, a.y, b.x, b.y);
    if (d > maxDist) { maxDist = d; maxIdx = i; }
  }
  if (maxDist > epsilon) {
    keep[maxIdx] = 1;
    rdpRange(pts, start, maxIdx, epsilon, keep);
    rdpRange(pts, maxIdx, end, epsilon, keep);
  }
}

/** Full recursive Douglas-Peucker simplification over a packed buffer's xy. */
export function rdpSimplify(points: Float32Array, epsilon: number): Point2[] {
  const n = points.length / 4;
  if (n === 0) return [];
  const pts: Point2[] = new Array(n);
  for (let i = 0; i < n; i++) pts[i] = { x: points[i * 4]!, y: points[i * 4 + 1]! };
  if (n <= 2) return pts;
  const keep = new Uint8Array(n);
  keep[0] = 1; keep[n - 1] = 1;
  rdpRange(pts, 0, n - 1, epsilon, keep);
  const out: Point2[] = [];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(pts[i]!);
  return out;
}

function packShape(pts: Point2[]): Float32Array {
  const out = new Float32Array(pts.length * 4);
  for (let i = 0; i < pts.length; i++) {
    out[i * 4] = pts[i]!.x;
    out[i * 4 + 1] = pts[i]!.y;
    out[i * 4 + 2] = 0.5;
    out[i * 4 + 3] = 0;
  }
  return out;
}

function pathLength(points: Float32Array, n: number): number {
  let len = 0;
  for (let i = 0; i < n - 1; i++) {
    len += Math.hypot(points[(i + 1) * 4]! - points[i * 4]!, points[(i + 1) * 4 + 1]! - points[i * 4 + 1]!);
  }
  return len;
}

function interiorAngleDeg(prev: Point2, cur: Point2, next: Point2): number {
  const v1x = prev.x - cur.x, v1y = prev.y - cur.y;
  const v2x = next.x - cur.x, v2y = next.y - cur.y;
  const len1 = Math.hypot(v1x, v1y), len2 = Math.hypot(v2x, v2y);
  if (len1 === 0 || len2 === 0) return 0;
  const cos = Math.max(-1, Math.min(1, (v1x * v2x + v1y * v2y) / (len1 * len2)));
  return (Math.acos(cos) * 180) / Math.PI;
}

/** corners: exactly 4 points, in path order (either winding). */
function fitRectangle(corners: Point2[]): Float32Array {
  const cx = (corners[0]!.x + corners[1]!.x + corners[2]!.x + corners[3]!.x) / 4;
  const cy = (corners[0]!.y + corners[1]!.y + corners[2]!.y + corners[3]!.y) / 4;

  // Dominant edge direction mod 90deg (rectangle symmetry) via the standard
  // angle-quadrupling circular-mean trick: multiplying by 4 maps the 90deg
  // period onto a full circle so opposite/perpendicular edges agree.
  let sumX = 0, sumY = 0;
  for (let i = 0; i < 4; i++) {
    const a = corners[i]!, b = corners[(i + 1) % 4]!;
    const ang = Math.atan2(b.y - a.y, b.x - a.x);
    sumX += Math.cos(4 * ang);
    sumY += Math.sin(4 * ang);
  }
  let rotation = Math.atan2(sumY, sumX) / 4; // in (-45deg, 45deg]
  const rotDeg = (rotation * 180) / Math.PI;
  if (Math.abs(rotDeg) < RECT_AXIS_TOLERANCE_DEG) rotation = 0;

  // Project corners into the de-rotated frame, take the bbox, rotate back.
  const cosN = Math.cos(-rotation), sinN = Math.sin(-rotation);
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const c of corners) {
    const dx = c.x - cx, dy = c.y - cy;
    const rx = dx * cosN - dy * sinN;
    const ry = dx * sinN + dy * cosN;
    if (rx < minX) minX = rx; if (rx > maxX) maxX = rx;
    if (ry < minY) minY = ry; if (ry > maxY) maxY = ry;
  }
  const local: Point2[] = [
    { x: minX, y: minY }, { x: maxX, y: minY }, { x: maxX, y: maxY }, { x: minX, y: maxY },
  ];
  const cosP = Math.cos(rotation), sinP = Math.sin(rotation);
  const out = local.map((p) => ({
    x: cx + p.x * cosP - p.y * sinP,
    y: cy + p.x * sinP + p.y * cosP,
  }));
  out.push(out[0]!);
  return packShape(out);
}

function fitCircleOrEllipse(cx: number, cy: number, rx: number, ry: number): Float32Array {
  const out: Point2[] = [];
  for (let i = 0; i < CIRCLE_SEGMENTS; i++) {
    const t = (i / CIRCLE_SEGMENTS) * Math.PI * 2;
    out.push({ x: cx + rx * Math.cos(t), y: cy + ry * Math.sin(t) });
  }
  out.push(out[0]!);
  return packShape(out);
}

/**
 * Attempt to recognize a hand-drawn stroke as a perfect line, rectangle, or
 * circle/ellipse (spec §2). Returns a packed replacement buffer (pressure
 * 0.5 constant, dt 0; closed shapes repeat the first point last) or null
 * when nothing matches closely enough.
 */
export function fitShape(points: Float32Array, opts?: { epsilon?: number }): Float32Array | null {
  const n = points.length / 4;
  if (n < 2) return null;
  const epsilon = opts?.epsilon ?? DEFAULT_EPSILON;

  const totalLen = pathLength(points, n);
  if (totalLen < MIN_STROKE_LENGTH) return null;

  const start: Point2 = { x: points[0]!, y: points[1]! };
  const end: Point2 = { x: points[(n - 1) * 4]!, y: points[(n - 1) * 4 + 1]! };
  const simplified = rdpSimplify(points, epsilon);

  // --- Line: 2 effective corners, or max chord deviation < 8% of chord length ---
  const chordLen = Math.hypot(end.x - start.x, end.y - start.y);
  let maxDev = 0;
  for (let i = 0; i < n; i++) {
    const d = perpDistance(points[i * 4]!, points[i * 4 + 1]!, start.x, start.y, end.x, end.y);
    if (d > maxDev) maxDev = d;
  }
  if (simplified.length <= 2 || (chordLen > 0 && maxDev < LINE_DEVIATION_FRAC * chordLen)) {
    return packShape([start, end]);
  }

  // --- Shared "closed-ish" gate for rectangle + circle/ellipse ---
  const endpointGap = Math.hypot(end.x - start.x, end.y - start.y);
  const closedIsh = totalLen > 0 && endpointGap < CLOSED_GAP_FRAC * totalLen;
  if (!closedIsh) return null;

  // Corner detection for closed-ish shapes needs its own simplification pass:
  // the raw stroke's first/last points are only *nearly* coincident (the user
  // lifted the pen close to, but short of, the start), so feeding them to
  // rdpSimplify as-is makes the top-level chord's direction essentially
  // noise-driven, which can spuriously split a straight edge in two. Forcing
  // an exact closure (duplicate the first point onto the end) makes the
  // degenerate len2===0 case in perpDistance kick in for the top-level split,
  // so the first cut lands on a genuine far point (a real corner) instead.
  const closedBuf = new Float32Array((n + 1) * 4);
  closedBuf.set(points);
  closedBuf[n * 4] = points[0]!;
  closedBuf[n * 4 + 1] = points[1]!;
  closedBuf[n * 4 + 2] = points[2]!;
  closedBuf[n * 4 + 3] = points[3]!;
  const closedCorners = rdpSimplify(closedBuf, epsilon);

  // --- Rectangle: 4-5 corners, all interior angles 90 +/- 20deg ---
  if (closedCorners.length === 4 || closedCorners.length === 5) {
    const corners = closedCorners.length === 5 ? closedCorners.slice(0, 4) : closedCorners;
    let anglesOk = true;
    for (let i = 0; i < 4; i++) {
      const prev = corners[(i + 3) % 4]!, cur = corners[i]!, next = corners[(i + 1) % 4]!;
      const a = interiorAngleDeg(prev, cur, next);
      if (Math.abs(a - 90) > RECT_ANGLE_TOLERANCE_DEG) { anglesOk = false; break; }
    }
    if (anglesOk) return fitRectangle(corners);
  }

  // --- Circle / Ellipse: >=6 simplified corners, radius variance < 20% of mean ---
  if (closedCorners.length >= CIRCLE_MIN_CORNERS) {
    let cx = 0, cy = 0;
    for (let i = 0; i < n; i++) { cx += points[i * 4]!; cy += points[i * 4 + 1]!; }
    cx /= n; cy /= n;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    let sumR = 0;
    const radii = new Array<number>(n);
    for (let i = 0; i < n; i++) {
      const x = points[i * 4]!, y = points[i * 4 + 1]!;
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
      const r = Math.hypot(x - cx, y - cy);
      radii[i] = r;
      sumR += r;
    }
    const meanR = sumR / n;
    if (meanR > 0) {
      let maxDevR = 0;
      for (const r of radii) { const d = Math.abs(r - meanR); if (d > maxDevR) maxDevR = d; }
      const varianceFrac = maxDevR / meanR;
      // Circle and ellipse are sequential alternatives, not an AND-gate: an
      // elongated ellipse is *expected* to have high deviation from a single
      // mean radius (that is what elongation is), so the <20% roundness test
      // only decides the CIRCLE case; a shape that fails it still gets a
      // chance at the ellipse fit via the independent x/y axis-ratio check.
      if (varianceFrac < CIRCLE_RADIUS_VARIANCE_FRAC) {
        return fitCircleOrEllipse(cx, cy, meanR, meanR);
      }
      const radiusX = (maxX - minX) / 2, radiusY = (maxY - minY) / 2;
      const bigger = Math.max(radiusX, radiusY), smaller = Math.min(radiusX, radiusY);
      const isEllipse = bigger > 0 && (bigger - smaller) / bigger > ELLIPSE_AXIS_DIFF_FRAC;
      if (isEllipse) {
        return fitCircleOrEllipse((minX + maxX) / 2, (minY + maxY) / 2, radiusX, radiusY);
      }
    }
  }

  return null;
}
